import React, { useEffect } from 'react';
import { useNavigate, Link, Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiUsers, FiLogOut, FiHome, FiMenu, FiX, FiMail, FiBookmark, FiSettings, FiSun, FiClock } = FiIcons;

const AdminLayout = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  // Check if user is authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  if (!isAuthenticated) {
    return null;
  }

  const menuItems = [
    { path: '/admin', label: 'Dashboard', icon: FiHome, exact: true },
    { path: '/admin/aktuelle-menus', label: 'Aktuelle Menüs', icon: FiCalendar },
    { path: '/admin/naechste-menus', label: 'Nächste Woche', icon: FiCalendar },
    { path: '/admin/templates', label: 'Menü-Templates', icon: FiBookmark },
    { path: '/admin/anmeldungen', label: 'Anmeldungen', icon: FiUsers },
    { path: '/admin/newsletter', label: 'E-Mail Newsletter', icon: FiMail },
    { path: '/admin/tage-konfiguration', label: 'Tage-Konfiguration', icon: FiSettings },
    { path: '/admin/feiertage', label: 'Feiertage', icon: FiSun },
    { path: '/admin/servicezeiten', label: 'Servicezeiten', icon: FiClock } // ✅ NEUE ROUTE
  ];

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-gray-50">
      {/* Mobile Menu Toggle */}
      <div className="lg:hidden bg-white border-b border-gray-200 p-4 flex items-center justify-between">
        <Link to="/" className="text-xl font-bold text-primary-600">
          Admin Panel
        </Link>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 rounded-md text-gray-700 hover:bg-gray-100"
        >
          <SafeIcon icon={mobileMenuOpen ? FiX : FiMenu} className="text-xl" />
        </button>
      </div>

      {/* Sidebar - Mobile */}
      {mobileMenuOpen && (
        <motion.div
          initial={{ x: -300, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -300, opacity: 0 }}
          className="lg:hidden fixed inset-0 z-50 bg-black bg-opacity-50"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div 
            className="bg-white w-64 h-full p-4 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-primary-600">Admin Panel</h2>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 rounded-md text-gray-700 hover:bg-gray-100"
              >
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
            </div>

            <nav className="space-y-1">
              {menuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-primary-50 hover:text-primary-600 ${
                    window.location.pathname === item.path || 
                    (item.exact && window.location.pathname === '/admin')
                      ? 'bg-primary-50 text-primary-600 font-medium'
                      : ''
                  }`}
                >
                  <SafeIcon icon={item.icon} className="text-xl" />
                  <span>{item.label}</span>
                </Link>
              ))}

              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50"
              >
                <SafeIcon icon={FiLogOut} className="text-xl" />
                <span>Abmelden</span>
              </button>
            </nav>
          </div>
        </motion.div>
      )}

      {/* Sidebar - Desktop */}
      <div className="hidden lg:flex lg:flex-col lg:w-64 lg:bg-white lg:border-r lg:border-gray-200 lg:p-4">
        <div className="mb-8 px-4">
          <h2 className="text-2xl font-bold text-primary-600">Admin Panel</h2>
        </div>

        <nav className="space-y-1 flex-1">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center space-x-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-primary-50 hover:text-primary-600 ${
                window.location.pathname === item.path || 
                (item.exact && window.location.pathname === '/admin')
                  ? 'bg-primary-50 text-primary-600 font-medium'
                  : ''
              }`}
            >
              <SafeIcon icon={item.icon} className="text-xl" />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="mt-auto pt-4">
          <div className="px-4 py-2 mb-2">
            <p className="text-sm text-gray-500">Angemeldet als:</p>
            <p className="text-sm font-medium truncate">{user?.email}</p>
          </div>

          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50"
          >
            <SafeIcon icon={FiLogOut} className="text-xl" />
            <span>Abmelden</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-4 lg:p-8 overflow-y-auto">
        <Outlet />
      </div>
    </div>
  );
};

export default AdminLayout;